import React, { useEffect, useState, useRef, useCallback } from 'react';
import { tokens, type } from '../theme';
import { SEASONS, AGE_GROUPS, U15_PLUS, COMPETITION_LABELS, shortDivision, DEFAULT_SEASON, DEFAULT_AGE_GROUP, DEFAULT_DIVISION_NAME, TIME_WINDOWS, resolveTimeWindow, defaultTimeWindow, DEFAULT_COMPETITION } from '../config/seasons';
import { readFavouriteTeam, writeFavouriteTeam } from '../utils/favouriteTeam';
import { Link, useSearchParams } from 'react-router-dom';
import FilterBar from '../components/FilterBar';
import FixtureCard from '../components/FixtureCard';
import FixtureSkeleton from '../components/FixtureSkeleton';
import DataFreshness from '../components/DataFreshness';
import { getGames, getConferences, getConferencesByDivision, getLeagueDivisions, getTeams, fetchSeasons } from '../services/api';
import useGameWeather from '../hooks/useGameWeather';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { trackTeamScheduleView } from '../utils/analytics';

import {
  Box,
  Container,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Paper,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider,
  Button,
  Fab,
  Stack,
  Checkbox,
  ListItemText,
  Avatar,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Default age group when nothing has been chosen and no favourite is stored.


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};



const PAGE_SIZE = 50;

const Schedule = () => {
  
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const weather = useGameWeather(games);
  // Season list comes from the API so a new season needs no frontend deploy;
  // SEASONS is the build-time fallback.
  const [seasons, setSeasons] = useState(SEASONS);
  useEffect(() => {
    let active = true;
    fetchSeasons()
      .then(data => { if (active && data?.seasons?.length) setSeasons(data.seasons); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const [conferences, setConferences] = useState([]);
  const [leagueDivisions, setLeagueDivisions] = useState([]);

  // A league_divisions row is per (division, competition, season), so a division id
  // carried over from another season may not exist in the selected one — which left
  // the Division select blank and the game list empty (e.g. the default id 9 is a
  // 25/26 row, absent from 26/27). Reconcile to a valid division once they load.
  useEffect(() => {
    if (!leagueDivisions.length) return;
    const current = searchParams.get('division');
    if (current && leagueDivisions.some(d => String(d.id) === String(current))) return;
    const preferred =
      leagueDivisions.find(d => d.division_name === DEFAULT_DIVISION_NAME) || leagueDivisions[0];
    if (preferred) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('division', String(preferred.id));
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueDivisions]);
  const [teams, setTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  
  
  const [totalGames, setTotalGames] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const loadMoreRef = useRef();
  const initialLoadRef = useRef(true);
  const previousAgeGroupsRef = useRef([]);
  const updatingTeamSelectionRef = useRef(false);
  
  
  
  
  // Track team schedule views when team filter is applied via URL
  // This tracks when users navigate to a team's schedule via URL parameter
  useEffect(() => {
    const teamParam = searchParams.get('team');
    if (teamParam && teamParam !== 'all' && teamsLoaded && teams.length > 0) {
      // Find the team that matches this ID
      const team = teams.find(t => t.ids.includes(parseInt(teamParam, 10)));
      if (team) {
        const ageGroups = getAgeGroups();
        // Track for the primary age group (avoid duplicate tracking)
        const primaryAgeGroup = ageGroups[0] || searchParams.get('age_group') || DEFAULT_AGE_GROUP;
        trackTeamScheduleView(parseInt(teamParam, 10), team.name, primaryAgeGroup);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('team'), teamsLoaded, teams]);
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Helper function to get age groups from URL parameters
  // Active time window, and the concrete dates it resolves to.
  const timeWindow = searchParams.get('when')
    || defaultTimeWindow(searchParams.get('season') || DEFAULT_SEASON);
  const dateWindow = resolveTimeWindow(timeWindow, searchParams.get('season') || DEFAULT_SEASON);

  const getAgeGroups = () => {
    const ageGroupParam = searchParams.get('age_group');
    if (!ageGroupParam) return [DEFAULT_AGE_GROUP];
    
    // Support both single age group and comma-separated multiple age groups
    return ageGroupParam.includes(',') 
      ? ageGroupParam.split(',').map(ag => ag.trim())
      : [ageGroupParam];
  };

  // Helper function to find team value based on URL parameter
  const getTeamValue = () => {
    const teamParam = searchParams.get('team');
    
    if (!teamParam || teamParam === 'all') return 'all';
    
    // If teams haven't loaded yet, return the original team parameter
    if (!teamsLoaded || teams.length === 0) {
      return teamParam;
    }
    
    // Find team that contains this ID
    const team = teams.find(t => t.ids.includes(parseInt(teamParam, 10)));
    return team ? team.ids.join(',') : teamParam;
  };

  // Helper function to get team name from team ID (for fallback when age groups change)
  const getTeamNameFromId = (teamId) => {
    // This would need to be implemented with a separate API call or cached data
    // For now, we'll return null and let the system handle it
    return null;
  };


  // Set default URL parameters on component mount
  useEffect(() => {
    const currentAgeGroup = searchParams.get('age_group');
    const currentLeagueDivision = searchParams.get('division');
    const currentTeam = searchParams.get('team');
    
    // Only set defaults if no parameters are present
    if (!currentAgeGroup && !currentLeagueDivision && !currentTeam) {
      // Previously this hardcoded a specific team id, which pointed at the wrong
      // team as soon as the season rolled over and age groups shifted up.
      const favourite = readFavouriteTeam();
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('age_group', favourite?.ageGroup || DEFAULT_AGE_GROUP);
        newParams.set('division', favourite?.divisionId || '9');
        if (favourite?.teamId) {
          newParams.set('team', favourite.teamId);
        }
        return newParams;
      });
    }
  }, []); // Empty dependency array - only run on mount

  // Mobile-specific initialization fix
  useEffect(() => {
    // Force re-render on mobile after a short delay to ensure proper initialization
    if (isMobile && leagueDivisions.length === 0) {
      const timer = setTimeout(() => {
        // Try to reload the data
        const season = searchParams.get('season') || DEFAULT_SEASON;
        getLeagueDivisions(season).then(data => {
          setLeagueDivisions(data || []);
        }).catch(err => {
          console.error('Mobile: Failed to reload league divisions:', err);
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, leagueDivisions.length]);
  
  // Load more games - defined with useCallback to avoid reference error
  const loadMoreGames = useCallback(async (offset = Array.isArray(games) ? games.length : 0, limit = PAGE_SIZE) => {
    setLoadingMore(true);
    
    const ageGroups = getAgeGroups();
    const conference = searchParams.get('conference') || 'all';
    const conferenceId = conference !== 'all' ? parseInt(conference, 10) : undefined;
    const leagueDivision = searchParams.get('division');
    const leagueDivisionId = leagueDivision ? parseInt(leagueDivision, 10) : undefined;
    const season = searchParams.get('season') || DEFAULT_SEASON;
    const team = searchParams.get('team') || 'all';
    const teamIdList = team !== 'all' ? team.split(',').map(Number) : undefined;
    const competition = searchParams.get('competition') || DEFAULT_COMPETITION;
    
    // Don't load more games if no division is selected
    if (!leagueDivisionId) {
      setLoadingMore(false);
      return;
    }
    
    const { games: moreGames } = await getGames(ageGroups, conferenceId, leagueDivisionId, season, teamIdList, offset, limit, competition, dateWindow);
    setGames(prev => [...(prev || []), ...(moreGames || [])]);
    setLoadingMore(false);
  }, [Array.isArray(games) ? games.length : 0, searchParams]);

  useEffect(() => {
    const loadData = async (retryCount = 0) => {
      try {
        const season = searchParams.get('season') || DEFAULT_SEASON;
        
        // If we have URL params but no division, set a default division
        const hasAnyParams = searchParams.toString().length > 0;
        const hasDivision = searchParams.get('division');
        if (hasAnyParams && !hasDivision) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('division', '9'); // Default to Homegrown Division
          setSearchParams(newParams);
          return;
        }
        
        
        const competition = searchParams.get('competition') || DEFAULT_COMPETITION;

        const [conferencesData, leagueDivisionsData] = await Promise.all([
          // Scoped to the competition: Flex has its own conference names, so an
          // unscoped list offers League's and the selection means nothing here.
          getConferences(season, null, competition).catch(err => {
            console.error('Error loading conferences:', err);
            return [];
          }),
          getLeagueDivisions(season).catch(err => {
            console.error('Error loading league divisions:', err);
            return [];
          })
        ]);
        
        
        setConferences(Array.isArray(conferencesData) ? conferencesData : []);
        setLeagueDivisions(Array.isArray(leagueDivisionsData) ? leagueDivisionsData : []);
        
        
      } catch (error) {
        console.error('Error loading data:', error);
        // Set empty arrays to prevent null/undefined states
        setConferences([]);
        setLeagueDivisions([]);
        
        // Retry once on mobile if data loading fails
        if (isMobile && retryCount === 0) {
          setTimeout(() => loadData(1), 1000);
        }
      }
    };
    
    loadData();
  }, [searchParams, isMobile]);
  
  useEffect(() => {
    const loadTeams = async () => {
      
      setTeamsLoaded(false);
      try {
        const ageGroups = getAgeGroups();
        const conference = searchParams.get('conference') || 'all';
        const conferenceId = conference !== 'all' ? parseInt(conference, 10) : undefined;
        const leagueDivision = searchParams.get('division');
        const leagueDivisionId = leagueDivision ? parseInt(leagueDivision, 10) : undefined;
        const season = searchParams.get('season') || DEFAULT_SEASON;
        
        const data = await getTeams(ageGroups, leagueDivisionId, season);
        setTeams(Array.isArray(data) ? data : []);
        setTeamsLoaded(true);
        
        // Update refs for next comparison
        previousAgeGroupsRef.current = [...ageGroups];
        initialLoadRef.current = false;
      } catch (error) {
        console.error('Error loading teams:', error);
        setTeams([]);
        setTeamsLoaded(true);
      }
    };
    
    loadTeams();
  }, [searchParams.get('age_group'), searchParams.get('division'), searchParams.get('season')]);
  
  // Update team selection when age groups change to include same team across all age groups
  useEffect(() => {
    if (!teamsLoaded || teams.length === 0) return;
    if (updatingTeamSelectionRef.current) return; // Prevent infinite loop
    
    const currentTeam = searchParams.get('team');
    if (!currentTeam || currentTeam === 'all') return;
    
    const currentTeamIds = currentTeam.split(',').map(Number);
    const currentAgeGroups = getAgeGroups();
    
    // Find the team name for the currently selected team IDs
    const currentTeamName = teams.find(team => 
      team.ids.some(id => currentTeamIds.includes(id))
    )?.name;
    
    if (!currentTeamName) {
      // The team in the URL isn't in this season/age group's list — a remembered
      // favourite from a previous season, or a shared link from another season. Left
      // alone, the Team select holds a value matching no option, so reconcile it the
      // same way a stale division is. Note the `teams.length === 0` guard above means
      // this only fires when there is a real list to validate against, so the param
      // survives seasons that simply have no fixtures published yet.
      updatingTeamSelectionRef.current = true;
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('team');
        return next;
      }, { replace: true });
      setTimeout(() => { updatingTeamSelectionRef.current = false; }, 100);
      return;
    }
    
    // Find all team IDs for this team name - the backend already filtered by age groups
    // so we should have the correct team IDs for the selected age groups
    const teamsForThisName = teams.filter(team => team.name === currentTeamName);
    const allTeamIds = teamsForThisName.flatMap(team => team.ids);
    
    
    // Update team selection if the team IDs have changed
    // This handles both expanding to multiple age groups and switching between different age groups
    const teamIdsChanged = JSON.stringify(allTeamIds.sort()) !== JSON.stringify(currentTeamIds.sort());
    const isInitialLoad = initialLoadRef.current;
    
    // Update team selection if team IDs have changed and we're not in initial load
    if (teamIdsChanged && !isInitialLoad) {
      
      // Set flag to prevent infinite loop
      updatingTeamSelectionRef.current = true;
      
      // Clear current games to force reload
      setGames([]);
      setLoading(true);
      
      setSearchParams(prev => {
        prev.set('team', allTeamIds.join(','));
        return prev;
      });
      
      // Reset flag after a short delay
      setTimeout(() => {
        updatingTeamSelectionRef.current = false;
      }, 100);
    }
  }, [teamsLoaded, teams, searchParams.get('age_group')]);
  
  // Load games when URL changes
  useEffect(() => {
    const loadGames = async () => {
      
      // Don't load games if we're still in the initial state (no URL params at all)
      const hasAnyParams = searchParams.toString().length > 0;
      const hasDivision = searchParams.get('division');
      
      // If we have URL params but no division, set a default division
      if (hasAnyParams && !hasDivision) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('division', '9'); // Default to Homegrown Division
        setSearchParams(newParams);
        return;
      }
      
      if (!hasAnyParams || !hasDivision) {
        return;
      }
      
      setLoading(true);
      try {
        const ageGroups = getAgeGroups();
        const conference = searchParams.get('conference') || 'all';
        const conferenceId = conference !== 'all' ? parseInt(conference, 10) : undefined;
        const leagueDivision = searchParams.get('division');
        const leagueDivisionId = leagueDivision ? parseInt(leagueDivision, 10) : undefined;
        const season = searchParams.get('season') || DEFAULT_SEASON;
        const team = searchParams.get('team') || 'all';
        const teamIdList = team !== 'all' ? team.split(',').map(Number) : undefined;
        const competition = searchParams.get('competition') || DEFAULT_COMPETITION;
        
        
        const { games: newGames, total } = await getGames(
          ageGroups,
          conferenceId,
          leagueDivisionId,
          season,
          teamIdList,
          0,
          PAGE_SIZE,
          competition,
          dateWindow
        );
        
        
        
        if (Array.isArray(newGames)) {
          // Filter games by requested age groups as a safety measure
          const filteredGames = newGames.filter(game => ageGroups.includes(game.age_group));
          
          // Remove duplicate games based on match_id
          const uniqueGames = [];
          const seenMatchIds = new Set();
          
          filteredGames.forEach(game => {
            if (!seenMatchIds.has(game.match_id)) {
              seenMatchIds.add(game.match_id);
              uniqueGames.push(game);
            }
          });
          
          
          setGames(uniqueGames);
        } else {
          setGames([]);
        }
        setTotalGames(total || 0);
      } catch (error) {
        console.error('Error loading games:', error);
        setGames([]);
        setTotalGames(0);
      } finally {
        setLoading(false);
      }
    };

    loadGames();
  }, [searchParams]);
  
  useEffect(() => {
    if (loadingMore || (Array.isArray(games) ? games.length : 0) >= totalGames) return;

    const currentRef = loadMoreRef.current;
    const observer = new window.IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && (Array.isArray(games) ? games.length : 0) < totalGames && !loadingMore) {
          loadMoreGames();
        }
      },
      { threshold: 1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [games, totalGames, loadingMore, loadMoreGames]);
  
  
  const handleAgeGroupChange = (event) => {
    setSearchParams(prev => {
      const selectedAgeGroups = event.target.value;
      const ageGroupsArray = Array.isArray(selectedAgeGroups) ? selectedAgeGroups : [selectedAgeGroups];
      
      if (Array.isArray(selectedAgeGroups)) {
        prev.set('age_group', selectedAgeGroups.join(','));
      } else {
        prev.set('age_group', selectedAgeGroups);
      }
      
      // Check if any selected age group is U15+ (supports competition filter)
      const u15PlusGroups = U15_PLUS;
      const hasU15Plus = ageGroupsArray.some(ag => u15PlusGroups.includes(ag));
      
      // If no U15+ age groups selected, remove competition parameter
      if (!hasU15Plus) {
        prev.delete('competition');
      }
      
      // When age groups change, update team selection to include all teams with the same name
      const currentTeam = prev.get('team');
      if (currentTeam && currentTeam !== 'all') {
        // We'll update the team selection after teams are loaded
        // This will be handled in the teams loading effect
      }
      
      return prev;
    });
  };
  
  const handleConferenceChange = (event) => {
    setSearchParams(prev => {
      prev.set('conference', event.target.value);
      return prev;
    });
  };
  
  const handleLeagueDivisionChange = (event) => {
    setSearchParams(prev => {
      const divisionId = parseInt(event.target.value, 10);
      prev.set('division', event.target.value);
      
      // If division is not Homegrown (ID 9), remove competition parameter
      if (divisionId !== 9) {
        prev.delete('competition');
      }
      
      // Keep the current team selection - let the backend handle filtering
      // The team will remain selected if it's valid for the new division
      // If it's not valid, the games will simply not show for that team
      return prev;
    });
  };
  
  const handleSeasonChange = (event) => {
    setSearchParams(prev => {
      prev.set('season', event.target.value);
      return prev;
    });
  };
  
  const handleTeamChange = (event) => {
    const teamId = event.target.value;
    setSearchParams(prev => {
      prev.set('team', teamId);
      return prev;
    });
    // Remember the pick so the next visit lands on this team. Replaces the old
    // hardcoded default team id, which broke on the season rollover.
    if (teamId && teamId !== 'all') {
      // The pill's value is the whole ids list joined, so match on that rather
      // than on the first id — with more than one id the old comparison never
      // matched and the name was stored as undefined.
      const picked = teams.find(t => (t.ids ? t.ids.join(',') : String(t.id)) === String(teamId));
      writeFavouriteTeam({
        teamId,
        teamIds: picked?.ids,
        // /api/teams returns `name`, not `team_name`.
        teamName: picked?.name,
        ageGroup: searchParams.get('age_group') || DEFAULT_AGE_GROUP,
        divisionId: searchParams.get('division') || '9',
      });
    }
  };
  
  const handleCompetitionChange = (event) => {
    setSearchParams(prev => {
      prev.set('competition', event.target.value);
      // Conference ids are per (name × competition), so one picked under League
      // is not in the Flex list and would leave the filter showing nothing.
      prev.set('conference', 'all');
      return prev;
    });
  };
  
  const formatScore = (game) => {
    if (game.home_score === 'TBD') {
      return 'TBD';
    } else {
      return `${game.home_score} : ${game.away_score}`;
    }
  };
  
  // The fixture a parent actually opened the app for: the next upcoming game, or the
  // most recent result if the season is over.
  const nextMatch = React.useMemo(() => {
    if (!Array.isArray(games) || games.length === 0) return null;
    // "Next match" is only meaningful relative to a team. With no team selected the
    // list is the whole division, so the card would be showing an arbitrary fixture.
    if (getTeamValue() === 'all') return null;
    const now = new Date();
    const upcoming = games
      .filter(g => g.date && new Date(g.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    if (upcoming.length) return upcoming[0];
    const past = games
      .filter(g => g.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    return past[0] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games, searchParams.get('team'), teamsLoaded]);

  const groupedGames = React.useMemo(() => {
    // The highlighted fixture stays in the list as well as the card above it. Pulling
    // it out left a hole in the chronology — scroll fifteen games deep and the match
    // you care about simply isn't where it belongs.
    const list = Array.isArray(games) ? games : [];
    const out = [];
    const seen = new Map();
    list.forEach((game, index) => {
      const d = game.date ? new Date(game.date) : null;
      const key = d ? d.toDateString() : 'unknown';
      if (!seen.has(key)) {
        const group = {
          key,
          label: d
            ? d.toLocaleDateString('en-US', {
                weekday: 'long', month: 'short', day: 'numeric',
              })
            : 'Date to be confirmed',
          games: [],
        };
        seen.set(key, group);
        out.push(group);
      }
      seen.get(key).games.push({ game, index });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games, nextMatch]);

  const renderGameCard = (game, index) => {
    const isHomeTeam = game.home_team_id === parseInt(searchParams.get('team'), 10);
    const isAwayTeam = game.away_team_id === parseInt(searchParams.get('team'), 10);

    return (
      <FixtureCard
        key={game.match_id}
        game={game}
        isMobile={isMobile}
        highlightHome={isHomeTeam}
        highlightAway={isAwayTeam}
        // Mark the fixture featured in the card above, so it's findable in the
        // list. Previously this marked "your team is playing", which with a team
        // filter active meant every single card carried the accent.
        selected={Boolean(nextMatch) && game.match_id === nextMatch.match_id}
        ageSuffix={getAgeGroups().length === 1 ? null : `(${game.age_group})`}
        dateStyle="timeOnly"
        variant="row"
        weather={weather[game.match_id] || null}
      />
    );
  };


  // Travel distance lives on the My Team page only. It is measured from the
  // viewer, so on a league-wide browse it is noise — a fixture 1,600 miles away
  // in another conference tells a parent nothing they can act on.

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>


      {/* Integrated Filters */}
      <FilterBar
        filters={[
          // Ordered by what actually gets changed: a parent wants their team first.
          // Previously Team sat 5th and was 272px off the right edge of the row.
          {
            key: 'team', label: 'Team',
            value: getTeamValue(),
            options: [{ value: 'all', label: 'All teams' }].concat(
              (Array.isArray(teams) ? teams : [])
                .map(t => ({ value: t.ids.join(','), label: t.name }))
            ),
            onChange: v => handleTeamChange({ target: { value: v } }),
            isDefault: v => v === 'all',
            nameAtDefault: true,
          },
          {
            key: 'when', label: 'When',
            value: timeWindow,
            options: TIME_WINDOWS,
            onChange: v => setSearchParams(prev => {
              const next = new URLSearchParams(prev);
              next.set('when', v);
              return next;
            }),
            isDefault: v => v === defaultTimeWindow(searchParams.get('season') || DEFAULT_SEASON),
          },
          {
            key: 'age', label: 'Age group', multiple: true,
            value: getAgeGroups(),
            options: AGE_GROUPS.map(x => ({ value: x, label: x })),
            onChange: v => handleAgeGroupChange({ target: { value: v } }),
            isDefault: v => Array.isArray(v) && v.length === 1 && v[0] === DEFAULT_AGE_GROUP,
          },
          {
            key: 'season', label: 'Season',
            value: searchParams.get('season') || DEFAULT_SEASON,
            options: seasons.map(x => ({ value: x, label: x })),
            onChange: v => handleSeasonChange({ target: { value: v } }),
            isDefault: v => v === DEFAULT_SEASON,
          },
          ...(getAgeGroups().some(ag => U15_PLUS.includes(ag)) ? [{
            key: 'competition', label: 'Competition',
            value: searchParams.get('competition') || DEFAULT_COMPETITION,
            options: [
              { value: 'all', label: 'All competitions' },
              { value: 'League', label: 'League' },
              { value: 'MLS NEXT Flex', label: 'Flex' },
            ],
            onChange: v => handleCompetitionChange({ target: { value: v } }),
            isDefault: v => v === DEFAULT_COMPETITION,
          }] : []),
          {
            key: 'division', label: 'Division',
            value: searchParams.get('division') || '',
            // "Division" is redundant inside a filter named Division, and
            // "Homegrown Division" was the widest pill in the row at 186px.
            options: (Array.isArray(leagueDivisions) ? leagueDivisions : [])
              .map(d => ({ value: String(d.id), label: shortDivision(d.division_name) })),
            onChange: v => handleLeagueDivisionChange({ target: { value: v } }),
            isDefault: v => {
              const preferred = leagueDivisions.find(d => d.division_name === DEFAULT_DIVISION_NAME)
                || leagueDivisions[0];
              return preferred ? String(preferred.id) === String(v) : true;
            },
          },
          {
            key: 'conference', label: 'Conference',
            value: searchParams.get('conference') || 'all',
            options: [{ value: 'all', label: 'All conferences' }].concat(
              (Array.isArray(conferences) ? conferences : [])
                .map(c => ({ value: String(c.id), label: c.conference_name }))
            ),
            onChange: v => handleConferenceChange({ target: { value: v } }),
            isDefault: v => v === 'all',
            nameAtDefault: true,
          },
        ]}
      />

      {loading ? (
        <FixtureSkeleton isMobile={isMobile} />
      ) : (Array.isArray(games) ? games.length : 0) === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          {/* An empty team list means the source hasn't published this age group for
              this season yet — telling the user to adjust their filters in that case
              blames them for something they can't fix. */}
          {teamsLoaded && teams.length === 0 ? (
            <>
              <Typography variant="h6">
                {`No ${getAgeGroups().join(', ')} fixtures yet for ${searchParams.get('season') || DEFAULT_SEASON}`}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                MLS NEXT hasn't published this age group's schedule for the season yet.
                It will appear here once they do.
              </Typography>
              {(searchParams.get('season') || DEFAULT_SEASON) !== SEASONS[SEASONS.length - 1] && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    const current = searchParams.get('season') || DEFAULT_SEASON;
                    const idx = SEASONS.indexOf(current);
                    const previous = SEASONS[idx + 1];
                    if (!previous) return;
                    setSearchParams(prev => {
                      const next = new URLSearchParams(prev);
                      next.set('season', previous);
                      next.delete('division');
                      next.delete('team');
                      next.delete('conference');
                      return next;
                    });
                  }}
                  sx={{ minHeight: 44, textTransform: 'none', color: tokens.navy, borderColor: tokens.navy }}
                >
                  {`View ${SEASONS[SEASONS.indexOf(searchParams.get('season') || DEFAULT_SEASON) + 1]} instead`}
                </Button>
              )}
            </>
          ) : timeWindow !== 'season' ? (
            /* An empty list here usually means a bye week, not a bad filter — the
               time window is hiding fixtures that exist. Say so, and offer the
               one tap that brings them back. */
            <>
              <Typography variant="h6">
                {timeWindow === 'week' ? 'No games this week' : 'No upcoming games'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                {timeWindow === 'week'
                  ? 'Nothing scheduled in the next seven days for these filters.'
                  : 'Every fixture for these filters has already been played.'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setSearchParams(prev => {
                  const next = new URLSearchParams(prev);
                  next.set('when', 'season');
                  return next;
                })}
                sx={{ minHeight: 44, textTransform: 'none', color: tokens.navy, borderColor: tokens.navy }}
              >
                Show full season
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6">No games found</Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your filters to see more results
              </Typography>
            </>
          )}
        </Paper>
      ) : (
        <Box>

          {/* The next-match card moved to the My Team tab, which is where a
              team-centric view belongs. It duplicated a row already in the list
              below and sat outside the content column. The nextMatch memo stays:
              it still marks that fixture in the list so it is findable. */}

          <Box sx={{ pb: 3 }}>
            {/* Grouped by day with a sticky header. The date was previously repeated
                on every card — the most repeated string on the page. */}
            {groupedGames.map(group => (
              <Card key={group.key} sx={{ mb: 1.5, overflow: 'hidden' }}>
                {/* The date lives inside the card, as a header for the day's fixtures,
                    rather than as a bare label floating between cards. */}
                <Box sx={{
                  px: isMobile ? 1.5 : 2,
                  py: 1,
                  backgroundColor: tokens.navyTint,
                  borderBottom: `1px solid ${tokens.rule}`,
                }}>
                  <Typography sx={{
                    fontSize: type.label,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: tokens.navy,
                  }}>
                    {group.label}
                  </Typography>
                </Box>
                {group.games.map(({ game, index }, i) => (
                  <Box
                    key={game.match_id}
                    sx={{ borderTop: i === 0 ? 'none' : `1px solid ${tokens.ruleSoft}` }}
                  >
                    {renderGameCard(game, index)}
                  </Box>
                ))}
              </Card>
            ))}
          </Box>
          {/* Infinite scroll sentinel */}
          <div ref={loadMoreRef} />
          <DataFreshness />
        </Box>
      )}
    </Box>
  );
};

export default Schedule;
